import { useState, useEffect } from 'react';
import { admin as adminApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, FileText } from 'lucide-react';

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const pageSize = 30;

  useEffect(() => {
    adminApi.logs({ skip: page * pageSize, limit: pageSize })
      .then(r => { setLogs(r.data.logs || []); setTotal(r.data.total || 0); })
      .catch(() => {});
  }, [page]);

  const actionColors = {
    config_update: 'bg-amber-500/20 text-amber-400',
    config_bulk_update: 'bg-amber-500/20 text-amber-400',
    user_update: 'bg-primary/20 text-primary',
  };

  return (
    <div data-testid="audit-logs" className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight font-['Plus_Jakarta_Sans']">Audit Logs</h2>
        <p className="text-muted-foreground mt-1">{total} total entries</p>
      </div>

      <Card className="bg-card border-border/50">
        <CardContent className="p-0">
          {logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <FileText className="w-8 h-8 mb-3 opacity-40" />
              <p className="text-sm">No audit logs yet</p>
            </div>
          ) : (
            <div className="divide-y divide-border/20">
              {logs.map((log, i) => (
                <div key={i} data-testid={`log-entry-${i}`} className="p-4 hover:bg-secondary/5 transition-colors">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <Badge className={actionColors[log.action] || 'bg-secondary text-secondary-foreground'} variant="default">
                        {log.action}
                      </Badge>
                      <span className="text-xs text-muted-foreground font-mono">{log.actor_email}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">{log.created_at ? new Date(log.created_at).toLocaleString() : '-'}</span>
                  </div>
                  <p className="text-sm font-mono text-muted-foreground">{log.resource}</p>
                  {(log.before || log.after) && (
                    <div className="mt-1 text-xs space-x-4">
                      {log.before && <span className="text-destructive/70">Before: {String(log.before).slice(0, 80)}</span>}
                      {log.after && <span className="text-emerald-400/70">After: {String(log.after).slice(0, 80)}</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {total > pageSize && (
            <div className="flex items-center justify-between p-3 border-t border-border/50">
              <span className="text-xs text-muted-foreground">Page {page + 1} of {Math.ceil(total / pageSize)}</span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => p - 1)} className="h-7">
                  <ChevronLeft className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="outline" disabled={(page + 1) * pageSize >= total} onClick={() => setPage(p => p + 1)} className="h-7">
                  <ChevronRight className="w-3 h-3" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
